// Create a strategy instance: kind (from the engine), a game found by sport + search (what rsh5 serves right
// now, with the top of book so the operator sees what they're about to quote), the bot user, unit, mode and
// params prefilled from the kind's example. Always created disarmed; arming is a separate click in the panel.

import { useCallback, useState } from 'react';
import { useVisibilityAwarePoll } from '@/hooks/useVisibilityAwarePoll';
import { rustLane, fmt, type Game, type Instance, type Json, type Kind } from '@/lib/rustLane/api';

const SPORTS = ['ATP', 'WTA', 'NFL', 'NBA', 'NHL', 'CFB', 'CBB', 'MLB', 'ESPORTS', 'SOCCER', 'UFC'];

export function NewStrategy({ users, onCreated, onNotice }: { users: string[]; onCreated: (inst: Instance) => void | Promise<void>; onNotice: (s: string) => void }) {
  const [kinds, setKinds] = useState<Kind[]>([]);
  const [kind, setKind] = useState('');
  const [sport, setSport] = useState('ATP');
  const [q, setQ] = useState('');
  const [games, setGames] = useState<Game[]>([]);
  const [gameKey, setGameKey] = useState('');
  const [user, setUser] = useState('');
  const [unit, setUnit] = useState(25);
  const [mode, setMode] = useState<'shadow' | 'live'>('shadow');
  const [params, setParams] = useState('{}');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // The kinds list and the game list are reads on the repo's poll hook: an immediate read on mount, a slow
  // refresh after, and a fresh read whenever sport or search changes (the callback identity changes).
  const loadKinds = useCallback(async (signal: AbortSignal) => {
    try {
      const k = await rustLane.kinds(signal);
      if (signal.aborted) return;
      setKinds(k);
      setKind((cur) => {
        if (cur) return cur;
        const first = k[0];
        if (first) setParams(JSON.stringify(first.example ?? first.params ?? {}, null, 2));
        return first?.name ?? '';
      });
    } catch (e) { if (!signal.aborted) onNotice(`kinds: ${e instanceof Error ? e.message : 'failed'}`); }
  }, [onNotice]);
  useVisibilityAwarePoll(loadKinds, 60_000);
  // No effect for the default user: the first bot user stands in until one is picked.
  const effUser = user || users[0] || '';

  const search = useCallback(async (signal: AbortSignal) => {
    try {
      const g = await rustLane.games(sport, q, signal);
      if (signal.aborted) return;
      setGames(g.filter((x) => x.markets?.some((m) => m.class === 'moneyline')));
    } catch (e) { if (!signal.aborted) onNotice(`games: ${e instanceof Error ? e.message : 'failed'}`); }
  }, [sport, q, onNotice]);
  useVisibilityAwarePoll(search, 15_000);

  const pickKind = (name: string) => {
    setKind(name);
    const k = kinds.find((x) => x.name === name);
    if (k) setParams(JSON.stringify(k.example ?? k.params ?? {}, null, 2));
  };

  const submit = async () => {
    let p: Json;
    try { p = JSON.parse(params) as Json; } catch { onNotice('params is not valid JSON'); return; }
    if (!kind || !gameKey || !effUser) { onNotice('pick a kind, a game and a user'); return; }
    setBusy(true);
    try {
      const target = gameKey.includes('|') ? { game: gameKey } : { event: gameKey };
      const inst = await rustLane.create({ kind, target, user: effUser, params: p, unit_usd: Math.max(1, Math.round(unit)), mode, reason: reason || 'streamrift mode' });
      onNotice(`created ${inst.id?.slice(0, 8) ?? ''} (disarmed)`);
      await onCreated(inst);
    } catch (e) { onNotice(`create: ${e instanceof Error ? e.message : 'failed'}`); } finally { setBusy(false); }
  };

  const selectedGame = games.find((g) => g.key === gameKey);
  return (
    <div className="space-y-2 font-mono text-xs">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">kind</span>
          <select id="ns-kind" value={kind} onChange={(e) => pickKind(e.target.value)} className="bg-muted rounded px-2 py-1">{kinds.map((k) => <option key={k.name} value={k.name}>{k.name}</option>)}</select></label>
        <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">user</span>
          <select id="ns-user" value={effUser} onChange={(e) => setUser(e.target.value)} className="bg-muted rounded px-2 py-1">{users.map((u) => <option key={u} value={u}>{u}</option>)}</select></label>
        <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">sport</span>
          <select id="ns-sport" value={sport} onChange={(e) => setSport(e.target.value)} className="bg-muted rounded px-2 py-1">{SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
        <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">search</span>
          <input id="ns-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="surname" className="bg-muted rounded px-2 py-1" /></label>
      </div>
      <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">game ({games.length} with a moneyline)</span>
        <select id="ns-game" value={gameKey} onChange={(e) => setGameKey(e.target.value)} className="bg-muted rounded px-2 py-1" size={Math.min(6, Math.max(2, games.length))}>
          {games.map((g) => <option key={g.key} value={g.key}>{fmt.gameLabel(g.key)} · {g.key.split('|')[1]}</option>)}
        </select></label>
      {selectedGame && (
        <div className="text-muted-foreground space-y-0.5">
          {selectedGame.markets.filter((m) => m.class === 'moneyline').slice(0, 4).map((m) => (
            <div key={m.ticker} className="flex justify-between"><span>{m.ticker.split('-').pop()} {m.subtitle ?? ''}</span><span>{fmt.cents(m.yesBid)} / {fmt.cents(m.yesAsk)}</span></div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">unit $</span>
          <input id="ns-unit" type="number" min={1} step={1} value={unit} onChange={(e) => setUnit(Number(e.target.value))} className="bg-muted rounded px-2 py-1" /></label>
        <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">mode</span>
          <select id="ns-mode" value={mode} onChange={(e) => setMode(e.target.value as 'shadow' | 'live')} className="bg-muted rounded px-2 py-1"><option value="shadow">shadow</option><option value="live">live (real orders once armed)</option></select></label>
      </div>
      <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">params</span>
        <textarea id="ns-params" value={params} onChange={(e) => setParams(e.target.value)} className="bg-muted h-40 rounded px-2 py-1" spellCheck={false} /></label>
      <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">reason (logged)</span>
        <input id="ns-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-muted rounded px-2 py-1" placeholder="why" /></label>
      <button type="button" disabled={busy} onClick={() => void submit()} className="w-full rounded bg-emerald-500/15 px-2 py-1.5 font-semibold text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-50">{busy ? 'creating…' : 'create (disarmed)'}</button>
    </div>
  );
}
